// src/view/fullscreen.ts — Tam ekran düğmesi + `F` kısayolu.
//
// Neden ayrı modül: menü, maç ve eğitim aynı düğmeyi paylaşıyor; her birine ayrı ayrı koymak yerine süreçte BİR kez
// kurulur (main.ts) ve sayfa boyunca sağ üstte durur. DOM'unu kendisi yaratır — index.html'e bağımlı değil.
//
// Tarayıcı gerçekleri:
//   • `requestFullscreen()` KULLANICI JESTİ ister. Fare tıklaması ve gerçek klavye basışı jest sayılır; **gamepad SAYILMAZ**
//     (pad girdisi rAF'ta yoklanır, tarayıcı bunu kullanıcı etkinliği kabul etmez). Bu yüzden duraklatma menüsündeki
//     "Tam ekran" düğmesine pad'in A'sıyla basılırsa istek sessizce reddedilir — düğme fare/klavye için oradadır.
//   • Safari'de ön ek gerekir (`webkitRequestFullscreen` / `webkitExitFullscreen`, `webkitFullscreenElement`).
//   • Tam ekranda Escape tarayıcıya gider (oyunun duraklatması için P ikincil tuş olarak zaten var — DESIGN §2).
//   • Tam ekrana girip çıkmak `resize` üretir; Stage zaten dinliyor, ek iş yok.

/** Ön ekli API'ler için yapısal tipler (@types/dom bunları bilmiyor). */
interface FsElement extends HTMLElement { webkitRequestFullscreen?: () => Promise<void> | void }
interface FsDocument extends Document { webkitExitFullscreen?: () => Promise<void> | void; webkitFullscreenElement?: Element | null }

const doc = (): FsDocument => document as FsDocument;

export function isFullscreen(): boolean {
  const d = doc();
  return !!(d.fullscreenElement ?? d.webkitFullscreenElement);
}

/** Tam ekran destekleniyor mu (iOS Safari'de iPhone'da yoktur). */
export function fullscreenSupported(): boolean {
  const el = document.documentElement as FsElement;
  return typeof el.requestFullscreen === 'function' || typeof el.webkitRequestFullscreen === 'function';
}

/**
 * Tam ekranı açar/kapatır. Reddedilirse (jest yok, izin yok) SESSİZ döner: konsolu kirletmemek için — özellikle
 * pad'den gelen sentetik tıklamada bu beklenen bir durum.
 */
export async function toggleFullscreen(target: HTMLElement = document.documentElement): Promise<boolean> {
  const d = doc();
  try {
    if (isFullscreen()) {
      await (d.exitFullscreen ? d.exitFullscreen() : d.webkitExitFullscreen?.());
      return false;
    }
    const el = target as FsElement;
    await (el.requestFullscreen ? el.requestFullscreen() : el.webkitRequestFullscreen?.());
    return true;
  } catch {
    return isFullscreen();   // reddedildi: gerçek durumu bildir
  }
}

const STYLE_ID = 'ro-fullscreen-style';
const CSS = `
  #ro-fs { position: fixed; top: 10px; right: 12px; z-index: 40; width: 34px; height: 34px; padding: 0;
    display: flex; align-items: center; justify-content: center; cursor: pointer;
    border: 1px solid rgba(255,255,255,.18); border-radius: 8px; background: rgba(10,13,19,.55); color: #e6ebf3;
    opacity: .45; transition: opacity .15s, background .15s; }
  #ro-fs:hover, #ro-fs:focus-visible { opacity: 1; background: rgba(36,48,73,.9); }
  #ro-fs svg { width: 18px; height: 18px; display: block; }
`;

/**
 * Sağ üste tam ekran düğmesini kurar ve `F` kısayolunu bağlar. Süreçte bir kez çağrılır (main.ts).
 * Simge duruma göre değişir (aç / kapat) ve tarayıcı tarafından (Escape ile) çıkılınca da güncellenir.
 */
export function mountFullscreenButton(): { dispose(): void } {
  if (typeof document === 'undefined' || !fullscreenSupported()) return { dispose() { /* yok */ } };

  if (!document.getElementById(STYLE_ID)) {
    const st = document.createElement('style');
    st.id = STYLE_ID; st.textContent = CSS;
    document.head.appendChild(st);
  }

  const btn = document.createElement('button');
  btn.id = 'ro-fs';
  btn.type = 'button';
  document.body.appendChild(btn);

  // İki simge: köşelere açılan oklar (gir) / içe dönen oklar (çık).
  const ICON_IN = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5"/></svg>';
  const ICON_OUT = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M9 4v5H4M15 4v5h5M9 20v-5H4M15 20v-5h5"/></svg>';

  const sync = (): void => {
    const on = isFullscreen();
    btn.innerHTML = on ? ICON_OUT : ICON_IN;
    btn.title = on ? 'Tam ekrandan çık (F)' : 'Tam ekran (F)';
    btn.setAttribute('aria-label', btn.title);
  };
  sync();

  const onClick = (): void => { void toggleFullscreen(); };
  // Düğme odakta kalırsa Space/Enter oyuna değil düğmeye gider (zıplarken tam ekran açılmasın) → tıklamadan sonra odağı bırak.
  const onBlurAfterClick = (): void => btn.blur();
  const onKey = (e: KeyboardEvent): void => {
    if (e.code !== 'KeyF' || e.ctrlKey || e.altKey || e.metaKey || e.repeat) return;
    const t = e.target as HTMLElement | null;
    if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT')) return;
    e.preventDefault();
    void toggleFullscreen();
  };

  btn.addEventListener('click', onClick);
  btn.addEventListener('click', onBlurAfterClick);
  addEventListener('keydown', onKey);
  document.addEventListener('fullscreenchange', sync);
  document.addEventListener('webkitfullscreenchange', sync);

  return {
    dispose(): void {
      btn.removeEventListener('click', onClick);
      btn.removeEventListener('click', onBlurAfterClick);
      removeEventListener('keydown', onKey);
      document.removeEventListener('fullscreenchange', sync);
      document.removeEventListener('webkitfullscreenchange', sync);
      btn.remove();
    },
  };
}
